import apiClient from './client';

// Types for terms API
export interface TermsInfoResponse {
  terms_version: string;
  privacy_version: string;
  terms_url: string;
  privacy_url: string;
  terms_content_hash: string;
  privacy_content_hash: string;
}

export interface TermsContentResponse {
  document_type: 'terms' | 'privacy';
  version: string;
  effective_date: string;
  content: string;
  content_hash: string;
}

export interface DownloadLogRequest {
  document_type: 'terms' | 'privacy';
  version: string;
  download_format: 'pdf' | 'print';
}

export interface AcceptTermsRequest {
  acceptance_type: 'signup' | 'account_connection' | 'landing_page_scan';
  terms_version: string;
  privacy_version: string;
  terms_content_hash: string;
  privacy_content_hash: string;
  checkbox_text: string;
}

export interface AcceptTermsResponse {
  success: boolean;
  message: string;
  terms_accepted_at: string;
  privacy_accepted_at: string;
}

export interface TermsStatusResponse {
  has_accepted_terms: boolean;
  has_accepted_privacy: boolean;
  terms_version: string | null;
  privacy_version: string | null;
  terms_accepted_at: string | null;
  privacy_accepted_at: string | null;
  needs_reaccept: boolean;
}

// Checkbox text constants - must match what's shown to users
export const SIGNUP_CHECKBOX_TEXT =
  "I agree to the Terms & Conditions and Privacy Policy";

export const ACCOUNT_CONNECTION_CHECKBOX_TEXT =
  "I agree to the Terms & Conditions and Privacy Policy";

export const LANDING_PAGE_SCAN_CHECKBOX_TEXT =
  "I agree to the Terms of Service and Privacy Policy";

export const termsApi = {
  /**
   * Get current terms version and content hashes
   */
  async getCurrentTerms(): Promise<TermsInfoResponse> {
    const response = await apiClient.get<TermsInfoResponse>('/terms/current');
    return response.data;
  },

  /**
   * Record acceptance of terms and conditions
   * Requires authentication - call after login/signup
   */
  async acceptTerms(request: AcceptTermsRequest): Promise<AcceptTermsResponse> {
    const response = await apiClient.post<AcceptTermsResponse>('/terms/accept', request);
    return response.data;
  },

  /**
   * Check user's terms acceptance status
   * Requires authentication
   */
  async getTermsStatus(): Promise<TermsStatusResponse> {
    const response = await apiClient.get<TermsStatusResponse>('/terms/status');
    return response.data;
  },

  /**
   * Helper to accept terms during signup
   * Gets current terms info and records acceptance
   */
  async acceptTermsForSignup(): Promise<AcceptTermsResponse> {
    const termsInfo = await this.getCurrentTerms();
    return this.acceptTerms({
      acceptance_type: 'signup',
      terms_version: termsInfo.terms_version,
      privacy_version: termsInfo.privacy_version,
      terms_content_hash: termsInfo.terms_content_hash,
      privacy_content_hash: termsInfo.privacy_content_hash,
      checkbox_text: SIGNUP_CHECKBOX_TEXT,
    });
  },

  /**
   * Helper to accept terms during account connection
   * Gets current terms info and records acceptance
   */
  async acceptTermsForAccountConnection(): Promise<AcceptTermsResponse> {
    const termsInfo = await this.getCurrentTerms();
    return this.acceptTerms({
      acceptance_type: 'account_connection',
      terms_version: termsInfo.terms_version,
      privacy_version: termsInfo.privacy_version,
      terms_content_hash: termsInfo.terms_content_hash,
      privacy_content_hash: termsInfo.privacy_content_hash,
      checkbox_text: ACCOUNT_CONNECTION_CHECKBOX_TEXT,
    });
  },

  /**
   * Helper to accept terms during landing page scan authorization
   * Gets current terms info and records acceptance
   */
  async acceptTermsForLandingPageScan(): Promise<AcceptTermsResponse> {
    const termsInfo = await this.getCurrentTerms();
    return this.acceptTerms({
      acceptance_type: 'landing_page_scan',
      terms_version: termsInfo.terms_version,
      privacy_version: termsInfo.privacy_version,
      terms_content_hash: termsInfo.terms_content_hash,
      privacy_content_hash: termsInfo.privacy_content_hash,
      checkbox_text: LANDING_PAGE_SCAN_CHECKBOX_TEXT,
    });
  },

  /**
   * Get full content for terms or privacy policy
   * No authentication required
   */
  async getContent(documentType: 'terms' | 'privacy'): Promise<TermsContentResponse> {
    const response = await apiClient.get<TermsContentResponse>(`/terms/content/${documentType}`);
    return response.data;
  },

  /**
   * Log a PDF download for audit trail
   * Works for both authenticated and anonymous users
   */
  async logDownload(request: DownloadLogRequest): Promise<void> {
    try {
      await apiClient.post('/terms/download-log', request);
    } catch (error) {
      // Don't fail if logging fails - it's not critical
      // But log with context for debugging
      console.warn('Failed to log download:', {
        documentType: request.document_type,
        version: request.version,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  },
};
